/** Mock payload for Linehaul Settings → Rates & Accessorial → L Codes → carrier detail. */

import type { ExpiredAccessorialHistory as ExpiredAccessorialHistoryType } from "../../hooks/react-query/settings/linehaul/useExpiredAccessorialHistory";
import type {
  LinehaulLCodeDestinationSicList,
  LinehaulLCodesCarrierDetailContent,
} from "../../hooks/react-query/settings/linehaul/useLCodeCarrierProfile";
import type { LCodeAccessorialDetail } from "../../hooks/react-query/settings/linehaul/useLCodeEditAccessorial";

type MockAddendumDocumentPreview = {
  url: string;
  name: string;
  size: number;
  type: string;
  lastModified: string;
};

type Addendum = {
  id: number;
  uploadedDate: string;
  uploadedBy: string;
};

export const mockAccessorialRates = {
  default: {
    carrierId: 95128,
    carrierCode: "L004",
    carrierName: "COVENANT TRANSPORT INC",
    isActive: true,
    stopOff: {
      id: 123,
      amount: 67,
      effectiveDateUtc: "2026-05-19T00:00:00",
      expiredDateUtc: null,
      createdBy: "5864ddf3-66e0-4cb7-9fdf-aa4828d6abba",
      createdOn: "2026-05-19T17:33:01.529106+00:00",
      modifiedBy: "5864ddf3-66e0-4cb7-9fdf-aa4828d6abba",
      modifiedOn: "2026-05-19T17:33:01.529106+00:00",
      isUpcomingExpiration: false,
    },
    rollover: {
      id: 119,
      amount: 125.75,
      effectiveDateUtc: "2026-05-18T00:00:00",
      expiredDateUtc: null,
      createdBy: null,
      createdOn: "2026-05-19T11:42:34.50416+00:00",
      modifiedBy: null,
      modifiedOn: "2026-05-19T11:42:34.50416+00:00",
      isUpcomingExpiration: false,
    },
    tonu: {
      id: 116,
      amount: 175.5,
      tonuCancellationWindow: 24,
      effectiveDateUtc: "2026-05-18T00:00:00",
      expiredDateUtc: null,
      createdBy: null,
      createdOn: "2026-05-19T11:45:32.017616+00:00",
      modifiedBy: null,
      modifiedOn: "2026-05-19T11:45:32.017616+00:00",
      isUpcomingExpiration: false,
    },
    detention: {
      id: 86,
      detentionFifteenMinuteIncrement: 18.75,
      detentionMaxTime: 250.5,
      detentionFreeTimeHours: 2,
      effectiveDateUtc: "2026-05-18T00:00:00",
      expiredDateUtc: null,
      createdBy: null,
      createdOn: "2026-05-19T11:47:14.830384+00:00",
      modifiedBy: null,
      modifiedOn: "2026-05-19T11:47:14.830384+00:00",
      isUpcomingExpiration: false,
    },
  },
} satisfies Record<string, LCodeAccessorialDetail>;

const mockAccessorialRatesDefault = mockAccessorialRates.default;

export const mockLinehaulLCodesCarrierDetail = {
  default: {
    carrier: {
      id: mockAccessorialRatesDefault.carrierId,
      name: mockAccessorialRatesDefault.carrierName,
      code: mockAccessorialRatesDefault.carrierCode,
    },
    pricingContact: {
      id: 1,
      firstName: "Mark",
      lastName: "Twain",
      email: "carrier@gmail.com",
      role: "(888) 123-4567",
    },
    accessorialRates: mockAccessorialRatesDefault,
    addendums: [
      {
        id: 1,
        code: "01/23/26",
        name: "Uploaded",
        driverId: 0,
        ratePerMile: null,
        order: null,
        loadBoardScheduleId: null,
      },
      {
        id: 2,
        code: "01/23/26",
        name: "Uploaded",
        driverId: 0,
        ratePerMile: null,
        order: null,
        loadBoardScheduleId: null,
      },
      {
        id: 3,
        code: "01/23/26",
        name: "Uploaded",
        driverId: 0,
        ratePerMile: null,
        order: null,
        loadBoardScheduleId: null,
      },
    ],
    originSics: [
      { id: 1, code: "ATL", laneCount: 30, isExpiring: true },
      { id: 2, code: "BOS", laneCount: 30 },
      { id: 3, code: "CHT", laneCount: 30 },
      { id: 4, code: "LAX", laneCount: 30 },
      { id: 5, code: "EWR", laneCount: 30 },
      { id: 6, code: "HTX", laneCount: 30, isExpiring: true },
      { id: 7, code: "ATB-SEFL", laneCount: 30 },
      { id: 8, code: "SFO", laneCount: 30 },
      { id: 9, code: "ONT", laneCount: 30 },
      { id: 10, code: "WSH-PITD", laneCount: 30 },
      { id: 11, code: "IND-DAFG", laneCount: 30, isExpiring: true },
      { id: 12, code: "ATM", laneCount: 30 },
      { id: 13, code: "HTX", laneCount: 30, isExpiring: true },
      { id: 14, code: "BOS", laneCount: 30 },
      { id: 15, code: "ONT", laneCount: 30, isExpiring: true },
      { id: 16, code: "ATL", laneCount: 30 },
      { id: 17, code: "BOS", laneCount: 30 },
      { id: 18, code: "ONT", laneCount: 30 },
    ],
    originSicTotalCount: 18,
  },
} satisfies Record<string, LinehaulLCodesCarrierDetailContent>;

export const destinationSicListByOrigin = {
  default: {
    totalDestinationSics: 27,
    destinations: {
      LAX: [
        {
          id: 1,
          serviceType: "Dedicated",
          rate: 2.15,
          status: "Upcoming",
          effectiveDate: "2026-04-01",
          expirationDate: "",
          isUpcomingExpiring: false,
          createdBy: "Jamie Chen",
          createdDate: "2026-02-02T10:00:00Z",
          modifiedBy: "Ravi Patel",
          modifiedDate: "2026-02-09T14:00:00Z",
        },
        {
          id: 2,
          serviceType: "1-way",
          rate: 1.95,
          status: "Active",
          effectiveDate: "2026-03-01",
          expirationDate: "2026-05-30",
          isUpcomingExpiring: true,
          createdBy: "Sophia Lee",
          createdDate: "2026-03-02T09:15:00Z",
          modifiedBy: "Jamie Chen",
          modifiedDate: "2026-03-05T16:20:00Z",
        },
        {
          id: 3,
          serviceType: "1-way",
          rate: 2.95,
          status: "Upcoming",
          effectiveDate: "2026-06-01",
          expirationDate: "2026-08-15",
          isUpcomingExpiring: false,
          createdBy: "Sophia Lee",
          createdDate: "2026-03-02T09:15:00Z",
          modifiedBy: "Jamie Chen",
          modifiedDate: "2026-03-05T16:20:00Z",
        },
      ],

      "IND-DAFG": [],

      ONT: [
        {
          id: 5,
          serviceType: "Dedicated",
          rate: 2.05,
          status: "Active",
          effectiveDate: "2026-02-15",
          expirationDate: "2027-01-31",
          isUpcomingExpiring: true,
          createdBy: "Chris Wong",
          createdDate: "2026-02-16T11:00:00Z",
          modifiedBy: "Chris Wong",
          modifiedDate: "2026-02-20T13:45:00Z",
        },
        {
          id: 6,
          serviceType: "Shuttle",
          rate: 1.88,
          status: "Upcoming",
          effectiveDate: "2026-04-01",
          expirationDate: "2027-03-31",
          isUpcomingExpiring: false,
          createdBy: "Sophia Lee",
          createdDate: "2026-04-02T08:30:00Z",
          modifiedBy: "Jamie Chen",
          modifiedDate: "2026-04-05T12:10:00Z",
        },
      ],

      HTX: [
        {
          id: 7,
          serviceType: "1-way",
          rate: 2.22,
          status: "Active",
          effectiveDate: "2026-05-01",
          expirationDate: "2027-04-30",
          isUpcomingExpiring: true,
          createdBy: "Ravi Patel",
          createdDate: "2026-05-02T10:20:00Z",
          modifiedBy: "Ravi Patel",
          modifiedDate: "2026-05-03T11:00:00Z",
        },
        {
          id: 8,
          serviceType: "Dedicated",
          rate: 2.48,
          status: "Upcoming",
          effectiveDate: "2026-06-01",
          expirationDate: "2026-12-31",
          isUpcomingExpiring: true,
          createdBy: "Chris Wong",
          createdDate: "2026-02-03T09:00:00Z",
          modifiedBy: "Sophia Lee",
          modifiedDate: "2026-02-01T17:30:00Z",
        },
      ],

      ATL: [],
    },
  },
} satisfies Record<string, LinehaulLCodeDestinationSicList>;

export const mockExpiredAccessorialHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 2,
    amount: "$120",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 3,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 4,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
];

export const mockAddendumDocumentPreview = {
  default: {
    url: "https://www.rd.usda.gov/sites/default/files/pdf-sample_0.pdf",
    name: "pdf-sample_0.pdf",
    size: 100,
    type: "application/pdf",
    lastModified: "2026-01-23T14:00:00Z",
  },
} as const satisfies { default: MockAddendumDocumentPreview };

export const mockAddendumsList = {
  default: [
    { id: 1, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 2, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 3, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 4, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 5, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 6, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 7, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 8, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 9, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 10, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 11, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 12, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 13, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 14, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
    { id: 15, uploadedDate: "01/23/26", uploadedBy: "Mark Twain" },
  ],
} satisfies { default: Addendum[] };

export const mockExpiredRatesLCodesHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 2,
    amount: "$120",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 3,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 4,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
];

export const mockExpiredRatesMCodesHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 2,
    amount: "$120",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 3,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 4,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
];

export const mockExpiredTollsLaneHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2025-06-01",
    expirationDate: "2025-12-31",
    expiredBy: "Alex Kim",
  },
  {
    id: 2,
    amount: "$100",
    effectiveDate: "2024-01-15",
    expirationDate: "2025-05-30",
    expiredBy: "Jamie Chen",
  },
  {
    id: 3,
    amount: "$100",
    effectiveDate: "2024-01-15",
    expirationDate: "2025-05-30",
    expiredBy: "Jamie Chen",
  },
  {
    id: 4,
    amount: "$100",
    effectiveDate: "2024-01-15",
    expirationDate: "2025-05-30",
    expiredBy: "Jamie Chen",
  },
];
